import { create } from "zustand";
import { devtools, subscribeWithSelector } from "zustand/middleware";
import { produce } from "immer";
import axios from "axios";

const initialState = {
  gettingBeneficiaries: false,
  beneficiariesError: null,
  beneficiaries: [],

  creatingBeneficiary: false,
  createBeneficiaryError: null,
  createdBeneficiary: {},
  showCreateBeneficiary: false,

  updatingBeneficiary: false,
  updateBeneficiaryError: null,
  updatedBeneficiary: null,
  beneficiaryToEdit: null,
  showUpdateBeneficiaries: false,

  deletingBeneficiary: false,
  deleteBeneficiaryError: null,
  deletedBeneficiary: null,

  bulkDeletingBeneficiaries: false,
  bulkDeleteBeneficiariesError: null,

  gettingBeneficiaryDetails: false,
  beneficiaryDetailsError: null,
  beneficiaryDetails: null,
  showBeneficiaryDetails: false,

  gettingBeneficiariesByProject: false,
  beneficiariesByProjectError: null,
  beneficiariesByProject: [],

  creating: false,
};

const store = (set, get) => ({
  ...initialState,

  // actions
  async getBeneficiaries() {
    set(
      produce((state) => {
        state.gettingBeneficiaries = true;
        state.beneficiariesError = null;
      })
    );
    try {
      const res = await axios({
        method: "GET",
        url: "/programs-mgt/beneficiaries",
      });

      set(
        produce((state) => {
          state.beneficiaries = res.data?.data || res.data || [];
          state.gettingBeneficiaries = false;
        })
      );
    } catch (error) {
      set(
        produce((state) => {
          state.beneficiariesError = error;
          state.gettingBeneficiaries = false;
        })
      );
    }
  },

  async getBeneficiariesByProject(projectId) {
    set(
      produce((state) => {
        state.gettingBeneficiariesByProject = true;
        state.beneficiariesByProjectError = null;
      })
    );
    try {
      const res = await axios({
        method: "GET",
        url: `/programs-mgt/beneficiaries/project/${projectId}`,
      });

      set(
        produce((state) => {
          state.beneficiariesByProject = res.data?.data || res.data || [];
          state.gettingBeneficiariesByProject = false;
        })
      );
    } catch (error) {
      set(
        produce((state) => {
          state.beneficiariesByProjectError = error;
          state.gettingBeneficiariesByProject = false;
        })
      );
    }
  },

  async createBeneficiary(body) {
    set(
      produce((state) => {
        state.creatingBeneficiary = true;
        state.createBeneficiaryError = null;
        state.createdBeneficiary = null;
      })
    );
    try {
      const res = await axios({
        method: "POST",
        url: "/programs-mgt/beneficiaries",
        data: body,
      });

      set(
        produce((state) => {
          state.createdBeneficiary = res.data || null;
          state.creatingBeneficiary = false;
        })
      );
      get().getBeneficiaries();
    } catch (error) {
      set(
        produce((state) => {
          state.createBeneficiaryError = error;
          state.creatingBeneficiary = false;
        })
      );
    }
  },

  async updateBeneficiary(body, id) {
    set(
      produce((state) => {
        state.updatingBeneficiary = true;
        state.updateBeneficiaryError = null;
        state.updatedBeneficiary = null;
      })
    );
    try {
      const res = await axios({
        method: "PUT",
        url: `/programs-mgt/beneficiaries/${id}`,
        data: body,
      });

      set(
        produce((state) => {
          state.updatedBeneficiary = res.data || null;
          state.updatingBeneficiary = false;
        })
      );
      get().getBeneficiaries();
    } catch (error) {
      set(
        produce((state) => {
          state.updateBeneficiaryError = error;
          state.updatingBeneficiary = false;
        })
      );
    }
  },

  async deleteBeneficiary(id) {
    set(
      produce((state) => {
        state.deletingBeneficiary = true;
        state.deleteBeneficiaryError = null;
        state.deletedBeneficiary = null;
      })
    );
    try {
      const res = await axios({
        method: "DELETE",
        url: `/programs-mgt/beneficiaries/${id}`,
      });

      set(
        produce((state) => {
          state.deletedBeneficiary = res.data || null;
          state.deletingBeneficiary = false;
        })
      );
      get().getBeneficiaries();
    } catch (error) {
      set(
        produce((state) => {
          state.deleteBeneficiaryError = error;
          state.deletingBeneficiary = false;
        })
      );
    }
  },

  async bulkDeleteBeneficiaries(ids) {
    set(
      produce((state) => {
        state.bulkDeletingBeneficiaries = true;
        state.bulkDeleteBeneficiariesError = null;
      })
    );
    try {
      await axios({
        method: "DELETE",
        url: "/programs-mgt/beneficiaries/bulk-delete",
        data: { ids },
      });

      set(
        produce((state) => {
          state.bulkDeletingBeneficiaries = false;
        })
      );
      get().getBeneficiaries();
      return true;
    } catch (error) {
      set(
        produce((state) => {
          state.bulkDeleteBeneficiariesError = error;
          state.bulkDeletingBeneficiaries = false;
        })
      );
      return false;
    }
  },

  async getBeneficiaryDetails(id) {
    set(
      produce((state) => {
        state.gettingBeneficiaryDetails = true;
        state.beneficiaryDetailsError = null;
      })
    );
    try {
      const res = await axios({
        method: "GET",
        url: `/programs-mgt/beneficiaries/${id}`,
      });

      set(
        produce((state) => {
          state.beneficiaryDetails = res || null;
          state.gettingBeneficiaryDetails = false;
        })
      );
    } catch (error) {
      set(
        produce((state) => {
          state.beneficiaryDetailsError = error;
          state.gettingBeneficiaryDetails = false;
        })
      );
    }
  },

  async downloadTemplate() {
    try {
      const res = await axios({
        method: "GET",
        url: "/programs-mgt/beneficiaries/template",
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "beneficiaries_template.xlsx";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Template download failed", error);
    }
  },

  async exportBeneficiaries() {
    try {
      const res = await axios({
        method: "GET",
        url: "/programs-mgt/beneficiaries/export",
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "beneficiaries.xlsx";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Export failed", error);
    }
  },

  async uploadBeneficiaries(file, projectId) {
    set(produce((state) => {
      state.creatingBeneficiary = true;
      state.createBeneficiaryError = null;
    }));
    try {
      const formData = new FormData();
      formData.append("file", file);
      await axios({
        method: "POST",
        url: `/programs-mgt/beneficiaries/upload?project_id=${projectId}`,
        data: formData,
      });
      set(produce((state) => { state.creatingBeneficiary = false; }));
      get().getBeneficiaries();
      return true;
    } catch (error) {
      set(produce((state) => {
        state.createBeneficiaryError = error;
        state.creatingBeneficiary = false;
      }));
      return false;
    }
  },

  setShowCreateBeneficiaries(show) {
    set(
      produce((state) => {
        state.showCreateBeneficiary = show;
      })
    );
  },
  setUpdateBeneficiaryData(data) {
    set(
      produce((state) => {
        state.beneficiaryToEdit = data;
      })
    );
  },
  setCreating(show = false, creating = false, dataToEdit = {}) {
    set(
      produce((state) => {
        state.creating = creating;
        state.showCreateBeneficiary = show;
        state.beneficiaryToEdit = dataToEdit;
      })
    );
  },
  setShowViewBeneficiaryDetails(show) {
    set(
      produce((state) => {
        state.showBeneficiaryDetails = show;
      })
    );
  },
});

const storeName = "BENEFICIARIES STORE";

const useBeneficiariesStore = create(
  devtools(subscribeWithSelector(store), { name: storeName }),
  {
    name: storeName,
  }
);

useBeneficiariesStore.subscribe(
  (state) => state.createdBeneficiary,
  (beneficiary) => {
    if (beneficiary) {
      useBeneficiariesStore.getState().getBeneficiaries();
    }
  }
);

useBeneficiariesStore.subscribe(
  (state) => state.deletedBeneficiary,
  (beneficiary) => {
    if (beneficiary) {
      useBeneficiariesStore.getState().getBeneficiaries();
    }
  }
);

useBeneficiariesStore.subscribe(
  (state) => state.updatedBeneficiary,
  (beneficiary) => {
    if (beneficiary) {
      useBeneficiariesStore.getState().getBeneficiaries();
      useBeneficiariesStore.getState().setCreating(false);
    }
  }
);

export default useBeneficiariesStore;
